import { Component, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { SavedDataInfo } from '../../../models/form/saved-data-info';
import { RequestsService } from '../../../services/api/requests.service';
import { RequestsFormComponent } from '../components/requests-form/requests-form.component';
import { RequestsDetailsComponent } from '../components/requests-details/requests-details.component';
import { RequestsDateComponent } from '../components/requests-date/requests-date.component';
import { FormNotesCustomersComponent } from '../../customers/components/notes-customers-form/notes-customers-form.component';
import { HttpParams } from '@angular/common/http';
import { AlertNotificationService } from '../../../services/alert-notification.service';
import { firstValueFrom } from 'rxjs';
import { RouteService } from 'src/app/services/route.service';
import { MainLayoutService } from 'src/app/services/main-layout.service';

@Component({
  selector: 'app-requests-edit-page',
  imports: [RequestsFormComponent, RequestsDetailsComponent, RequestsDateComponent, FormNotesCustomersComponent, RouterLink],
  templateUrl: './requests-edit-page.component.html',
  styleUrl: './requests-edit-page.component.scss'
})
export class RequestsEditPageComponent implements OnDestroy {
  requestId!: number
  request?: any;
  requestNotes?: any;
  category?: any;
  
  private alertTimers: Map<string, any> = new Map<string, any>();

  @ViewChild(RequestsFormComponent) requestsFormComponent!: RequestsFormComponent;
  @ViewChild(RequestsDetailsComponent) requestsDetailsComponent!: RequestsDetailsComponent;
  @ViewChild(RequestsDateComponent) requestsDateComponent!: RequestsDateComponent;
  constructor(
    private route: ActivatedRoute,
    protected requestsService: RequestsService,
    private alertNotificationService: AlertNotificationService,
    private routeService: RouteService,
    private mainLayoutService: MainLayoutService
  ) {
  }
  ngOnInit(): void {
    const params = new HttpParams().set('size', '9999');
    this.requestId = Number(this.route.snapshot.paramMap.get('requestId'));
    if (this.requestId) {
      this.loadData(this.requestId);
      this.loadNotes(this.requestId, params);
    }
  }

  loadData(requestId: any) {
    this.requestsService.detail(requestId).subscribe((response: any) => {
      this.request = response.data?.request;
      this.category = response.data?.requests?.date?.category;
      this.mainLayoutService.setRelatedCodeItem(this.request?.code || null);
      this.mainLayoutService.setRelatedClientName(this.request?.customer?.companyName || null);
    });
  }

  loadNotes(requestId: any, params: HttpParams) {
    this.requestsService.listNotes(requestId, { params }).subscribe(response => {
      if (response.status.code === 200) {        
        this.requestNotes = response?.data.sectionNotes || [];
      }
    });
  }
  
  private showComponentAlert(component: any, type: string, message: string): void {
    const componentId = this.getComponentIdentifier(component);
    if (this.alertTimers.has(componentId)) {
      clearTimeout(this.alertTimers.get(componentId));
      this.alertTimers.delete(componentId);
    }
    
    if (component && component.showAlertInfo) {
      component.showAlertInfo(type, message);
      
      const timerId = setTimeout(() => {
        if (component && component.alertInfo) {
          component.alertInfo.show = false;
        }
        this.alertTimers.delete(componentId);
      }, 5000);
      
      this.alertTimers.set(componentId, timerId);
    }
  }
  
  private getComponentIdentifier(component: any): string {
    if (component === this.requestsFormComponent) return 'form';
    if (component === this.requestsDetailsComponent) return 'details';
    if (component === this.requestsDateComponent) return 'date';
    return 'unknown';
  }
  
  private closeAllAlerts(): void {
    const components = [
      this.requestsFormComponent,
      this.requestsDetailsComponent,
      this.requestsDateComponent
    ];
    
    components.forEach(component => {
      if (component && component.alertInfo) {
        component.alertInfo.show = false;
      }
      
      const componentId = this.getComponentIdentifier(component);
      if (this.alertTimers.has(componentId)) {
        clearTimeout(this.alertTimers.get(componentId));
        this.alertTimers.delete(componentId);
      }
    });
  }
  
  onRequestFormSavedData(savedDataInfo: SavedDataInfo) {
    if (savedDataInfo.success) {
      const requestId = savedDataInfo.response?.data?.request?.requestId || this.requestId;
      if (requestId) {
        this.loadData(requestId);
      }
    }
  }
  
  async submitAllForms(): Promise<void> {
    try {
      this.closeAllAlerts();
      
      const formComponents = [
        this.requestsFormComponent,
        this.requestsDetailsComponent,
        this.requestsDateComponent
      ];

      let allFormsValid = true;
      const originalFormValues = formComponents.map(component =>
        component && component.form ? { ...component.form.value } : null
      );

      const formDataResults = [];

      for (let i = 0; i < formComponents.length; i++) {
        const component = formComponents[i];
        if (!component || !component.form) continue;

        const formGroup = component.form;
        Object.keys(formGroup.controls).forEach(key => {
          const control = formGroup.get(key);
          control?.markAsTouched();
          control?.updateValueAndValidity();
        });

        if (originalFormValues[i]) {
          component.form.patchValue(originalFormValues[i]);
        }

        if (component === this.requestsFormComponent) {
          const customerId = component.form.get('customerId')?.value;
          if (customerId === null) {
            allFormsValid = false;
            this.showComponentAlert(component, 'danger', 'Please select an existing company or create a new one before proceeding');
            continue;
          }
        }

        if (component === this.requestsDateComponent) {
          const categoriesValid = component.validateCategories();
          if (!categoriesValid) {
            allFormsValid = false;
            this.showComponentAlert(component, 'danger', 'You must select at least one category');
          }
        }

        if (!formGroup.valid) {
          allFormsValid = false;
          this.showComponentAlert(component, 'danger', 'Check the required fields in this form');
        } else {
          let formData = {
            ...originalFormValues[i],
            tenantId: 1
          };
          if (component === this.requestsDateComponent && formData.taking === true && this.requestsDateComponent.currentUser) {
            formData = {
              ...formData,
              takingCareUserId: this.requestsDateComponent.currentUser.userId
            };
          }
          
          formDataResults.push(formData);
        }
      }

      if (!allFormsValid) {
        return;
      }

      const combinedData = formDataResults.reduce((acc, result) => {
        return result ? { ...acc, ...result } : acc;
      }, {});

      const isUpdate = !!this.requestId;
      const apiCall = isUpdate
        ? this.requestsService.update(this.requestId, combinedData)
        : this.requestsService.create(combinedData);

      apiCall.subscribe({
        next: (response) => {

          formComponents.forEach(component => {
            if (component) {
              this.showComponentAlert(component, 'success', 'Operation successfully completed');
            }
          });
          if (!isUpdate && response?.data?.request?.requestId) {
            this.requestId = response.data.request.requestId;
          }
          if (this.requestId) {
            setTimeout(() => {              
            this.routeService.requestsDetail(this.requestId)
            }, 500);
          }
        },
        error: (error) => {
          console.error('Error while sending:', error);

          if (error.error?.error?.validationErrors) {
            for (const component of formComponents) {
              if (component && component.form && component.updateValidationErrors) {
                const formControls = Object.keys(component.form.controls);
                const relevantErrors = error.error.error.validationErrors.filter(
                  (validationError: any) => formControls.includes(validationError.field)
                );

                if (relevantErrors.length > 0) {
                  component.updateValidationErrors(relevantErrors);
                  this.showComponentAlert(component, 'danger', 'Correct errors in the form');
                }
              }
            }
          } else {
            const firstComponent = formComponents.find(c => c && c.showAlertInfo);
            if (firstComponent) {
              this.showComponentAlert(firstComponent, 'danger', error.error?.error?.message || 'An error occurred while saving');
            } else {
              this.alertNotificationService.error('An error occurred while saving the request');
            }
          }
        }
      });
    } catch (error) {
      console.error('Unexpected error:', error);
      this.alertNotificationService.error('An unexpected error occurred');
    }
  }

  gotoViewRequest(): void {
    if (!this.requestId) {
      return;
    }
    this.routeService.requestsDetail(this.requestId);
  }

  ngOnDestroy(): void {
    this.mainLayoutService.setRelatedCodeItem(null);
    this.mainLayoutService.setRelatedClientName(null);
  }
}
